/**
 * Provider options for the calendar Session form, scoped to the selected
 * episode of care ("Case"): every Practitioner on the episode's CareTeam(s),
 * always including the episode's `careManager` (the default selection).
 */

import type { CareTeam } from '@medplum/fhirtypes';
import { useSearchResources } from '@medplum/react-hooks';
import { useMemo } from 'react';
import { getPractitionerParticipantRefs } from '@unify/fhir-model/resources/care-team';
import type { LiveCalendarEpisodeDetail } from './useCalendarAppointmentFormOptions';

export interface EpisodeCareTeamProviderOption {
  /** Practitioner id (no `Practitioner/` prefix). */
  value: string;
  label: string;
}

/**
 * Pure mapping: CareTeam practitioner participants + the episode careManager →
 * deduped, sorted select options. Labels prefer the live id→display lookup,
 * then participant and careManager reference displays.
 */
export function buildEpisodeCareTeamProviderOptions(
  careTeams: CareTeam[],
  episode: Pick<LiveCalendarEpisodeDetail, 'careManagerId' | 'careManagerDisplay'> | null,
  practitionerDisplayById: Map<string, string>,
): EpisodeCareTeamProviderOption[] {
  const labelById = new Map<string, string>();
  for (const team of careTeams) {
    for (const ref of getPractitionerParticipantRefs(team)) {
      const id = ref.reference?.slice('Practitioner/'.length).trim();
      if (!id) continue;
      const label = practitionerDisplayById.get(id) || ref.display?.trim();
      if (!labelById.has(id) || (label && !labelById.get(id))) {
        labelById.set(id, label ?? '');
      }
    }
  }
  const careManagerId = episode?.careManagerId;
  if (careManagerId && !labelById.get(careManagerId)) {
    labelById.set(
      careManagerId,
      practitionerDisplayById.get(careManagerId) || episode?.careManagerDisplay || '',
    );
  }
  return [...labelById.entries()]
    .map(([id, label]) => ({ value: id, label: label || `Practitioner ${id.slice(0, 8)}…` }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export interface UseEpisodeCareTeamProvidersResult {
  options: EpisodeCareTeamProviderOption[];
  loading: boolean;
}

export function useEpisodeCareTeamProviders(
  episode: LiveCalendarEpisodeDetail | null,
  practitionerDisplayById: Map<string, string>,
): UseEpisodeCareTeamProvidersResult {
  /** `useSearchResources` has no skip/enabled option and an undefined query
   * would search ALL CareTeams, so use a non-existent sentinel id when no
   * episode (or no team) is selected — it returns an empty result. */
  const careTeamIdQuery = episode?.careTeamIds.join(',') || 'none';
  const [careTeams] = useSearchResources('CareTeam', { _id: careTeamIdQuery });

  return useMemo(
    () => ({
      options: buildEpisodeCareTeamProviderOptions(careTeams ?? [], episode, practitionerDisplayById),
      loading: episode != null && careTeams === undefined,
    }),
    [careTeams, episode, practitionerDisplayById],
  );
}
